import * as React from 'react';
import { useState } from 'react';
import { TApiTaskDetailEntity } from '../../types/Tasks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { fetchWithAuthentication } from '../../actions/BaseAction';
import { routes } from '../../Routes';
import { getConclusionIcon, CONCLUSION_OPTIONS } from '../../helpers/conclusionStatus';
import SlideToConfirm from './SlideToConfirm';

interface IServiceConclusionSectionProps {
  task: TApiTaskDetailEntity;
  onSubmitConclusion: (conclusionStatus: number) => void;
  readOnly: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: '#e3f2fd',
      borderRadius: '8px',
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
    statement: {
      marginBottom: theme.spacing(2),
      fontStyle: 'italic',
    },
    radioGroup: {
      marginBottom: theme.spacing(2),
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    confirmedBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    equipmentInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.5),
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit',
      '&:hover': {
        backgroundColor: '#eeeeee',
      },
    },
    equipmentThumb: {
      width: '70px !important',
      minWidth: '70px !important',
      height: '70px !important',
      borderRadius: '4px',
      objectFit: 'cover' as const,
      flexShrink: 0,
    },
    equipmentDetails: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    equipmentName: {
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    equipmentMeta: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
  })
);

const ServiceConclusionSection: React.FC<IServiceConclusionSectionProps> = ({
  task,
  onSubmitConclusion,
  readOnly,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [equipmentImageSrc, setEquipmentImageSrc] = useState<string | null>(null);
  const isAlreadyConcluded = task.conclusionStatusValue != null;

  const equipmentUrl = task.parentEquipmentId
    ? routes.equipmentItem.replace(':id', String(task.parentEquipmentId))
    : null;

  React.useEffect(() => {
    let blobUrl: string | null = null;
    if (task.parentEquipmentImage) {
      fetchWithAuthentication(task.parentEquipmentImage)
        .then((blob) => {
          if (blob !== null) {
            blobUrl = URL.createObjectURL(blob);
            setEquipmentImageSrc(blobUrl);
          }
        })
        .catch(() => {});
    }
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [task.parentEquipmentImage]);

  return (
    <>
      {task.parentEquipmentName && (
        <Box
          className={classes.equipmentInfo}
          onClick={equipmentUrl ? () => history.push(equipmentUrl) : undefined}
          style={equipmentUrl ? undefined : { cursor: 'default' }}
        >
          {equipmentImageSrc && (
            <img
              src={equipmentImageSrc}
              alt={task.parentEquipmentName}
              className={classes.equipmentThumb}
            />
          )}
          <Box className={classes.equipmentDetails}>
            <Typography className={classes.equipmentName}>
              {task.parentEquipmentName}
            </Typography>
            {task.parentEquipmentModel && (
              <Typography className={classes.equipmentMeta}>
                {task.parentEquipmentModel}
              </Typography>
            )}
            {task.parentEquipmentSerialVin && (
              <Typography className={classes.equipmentMeta}>
                {task.parentEquipmentSerialVin}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {task.conclusion && (
        <Box className={classes.container}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            {t('tasks.service.conclusion_title')}
          </Typography>
          {task.conclusionStatement && (
            <Typography className={classes.statement}>
              {task.conclusionStatement}
            </Typography>
          )}

          {isAlreadyConcluded ? (
            <Box className={classes.confirmedBox}>
              {getConclusionIcon(task.conclusionStatusValue!)}
              <Typography>
                {task.conclusionStatusLabel}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {task.conclusionDateFormatted} {task.conclusionUser}
              </Typography>
            </Box>
          ) : (
            <>
              <RadioGroup
                className={classes.radioGroup}
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {CONCLUSION_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={
                      <span className={classes.radioLabel}>
                        {option.icon}
                        {t(option.translationKey)}
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
              {!readOnly && selectedStatus && (
                isMobile ? (
                  <SlideToConfirm
                    label={t('tasks.service.slide_to_confirm')}
                    onConfirm={() => onSubmitConclusion(parseInt(selectedStatus))}
                    confirmed={isAlreadyConcluded}
                  />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onSubmitConclusion(parseInt(selectedStatus))}
                  >
                    {t('tasks.service.confirm')}
                  </Button>
                )
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default ServiceConclusionSection;
